import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'wouter';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { authenticate, loadSalt } from './authenticationReducer';
import authenticationSelector from './authenticationSelector';
import type { AppDispatch } from '../store';
import PasswordField from '../common/components/PasswordField';
import encryption from '../common/helpers/encryption';
import transformer from '../common/helpers/transformer';

const reactAppVersion = process.env.REACT_APP_VERSION;

function Login() {
  const dispatch: AppDispatch = useDispatch();
  const [, setLocation] = useLocation();

  const isLoading = useSelector(authenticationSelector.isLoginLoading);
  const errorMessage = useSelector(authenticationSelector.getErrorMessage);

  const salt = useSelector(authenticationSelector.salt);
  const [password, setPassword] = React.useState('');

  const login = async () => {
    if (password.length > 0 && salt != null) {
      const { key } = await encryption.initKeys(password, transformer.fromBase64ToUint8Array(salt));
      const response = await dispatch(authenticate(transformer.fromUint8ArrayToBase64(key)));
      if (response.meta.requestStatus === 'fulfilled') {
        navigate();
      }
    }
  };

  const navigate = () => {
    const urlParams = new URLSearchParams(location.search);
    const redirect = urlParams.get('redirect');
    if (reactAppVersion === 'fhbf') {
      setLocation(redirect || '/startpage');
    } else {
      setLocation(redirect || '/clients');
    }
  };

  React.useEffect(() => {
    if (isLoading === false && salt == null) {
      dispatch(loadSalt());
    }
  }, [isLoading, salt]);

  return (
    <>
      <Typography fontSize='1.5rem' mb={2}>
        Login
      </Typography>
      <PasswordField
        fullWidth
        autoFocus
        value={password}
        handleChange={setPassword}
        error={errorMessage != null}
        helperText={errorMessage}
        onKeyPress={async (keyEvent) => {
          if (keyEvent.key === 'Enter') {
            await login();
          }
        }}
      />
      <Button
        variant='contained'
        disabled={isLoading}
        onClick={async () => {
          await login();
        }}
        sx={{ marginTop: '16px', marginLeft: 'auto' }}
      >
        Anmelden
      </Button>
    </>
  );
}

export default Login;
